import { useEffect, useState } from "react";
import { Loader2, Plus, Trash2, Save } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";

const eur = (v) => (v == null ? "—" : Number(v).toLocaleString("fr-FR", { style: "currency", currency: "EUR" }));
const inputCls = "w-full bg-[#0b0811] border border-white/10 text-white rounded-lg px-2.5 py-1.5 text-xs focus:border-[#f7c935] outline-none";
const NUM_FIELDS = [
  ["overtime_25", "Heures sup. 25 %"], ["overtime_50", "Heures sup. 50 %"], ["absence_hours_unpaid", "Absence non rémunérée (h)"], ["cp_days_taken", "Congés payés pris (j)"],
  ["meal_vouchers_count", "Titres-restaurant (nb)"], ["advance", "Acompte versé (€)"], ["transport_reimbursement", "Transport 50 % (€)"], ["expense_reimbursement", "Frais pro (€)"],
  ["benefits_in_kind", "Avantages en nature (€)"],
];

export function PayslipInputsDialog({ api, payslipId, onClose }) {
  const [ps, setPs] = useState(null);
  const [inputs, setInputs] = useState({});
  const [saving, setSaving] = useState(false);
  const [changed, setChanged] = useState(false);
  const [showLines, setShowLines] = useState(false);

  useEffect(() => {
    api.get(`/payroll/payslips/${payslipId}`).then((r) => { setPs(r.data); setInputs({ bonuses: [], manual_lines: [], ...(r.data.inputs || {}) }); }).catch(() => toast.error("Bulletin introuvable"));
  }, [api, payslipId]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put(`/payroll/payslips/${payslipId}/inputs`, inputs);
      setPs(r.data);
      setChanged(true);
      toast.success(`Recalculé · net à payer ${eur(r.data.totals.net_to_pay)}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Recalcul impossible");
    } finally {
      setSaving(false);
    }
  };

  const setNum = (k, v) => setInputs((s) => ({ ...s, [k]: v }));
  const bonuses = inputs.bonuses || [];
  const manual = inputs.manual_lines || [];
  const t = ps?.totals;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose(changed)}>
      <DialogContent className="bg-[#14101c] border-white/10 text-white max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="payslip-inputs-dialog">
        <DialogHeader><DialogTitle className="text-white">Saisie du mois — {ps?.employee?.first_name} {ps?.employee?.last_name} · {ps?.period}</DialogTitle></DialogHeader>
        {!ps ? <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-[#f7c935]" /></div> : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {NUM_FIELDS.map(([k, label]) => (
                <label key={k} className="text-[11px] text-slate-400">{`${label}`}
                  <input type="number" step="0.01" min="0" value={inputs[k] ?? ""} onChange={(e) => setNum(k, e.target.value)} className={`${inputCls} mt-1`} data-testid={`input-${k}`} />
                </label>
              ))}
            </div>
            <div>
              <div className="flex items-center justify-between mb-1"><span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Primes</span>
                <button onClick={() => setInputs((s) => ({ ...s, bonuses: [...bonuses, { label: "Prime", amount: "" }] }))} className="text-[11px] text-[#f7c935] flex items-center gap-1" data-testid="add-bonus-btn"><Plus className="w-3 h-3" /> Ajouter</button></div>
              {bonuses.map((b, i) => (
                <div key={i} className="flex gap-2 mb-1">
                  <input value={b.label} onChange={(e) => setInputs((s) => ({ ...s, bonuses: bonuses.map((x, j) => j === i ? { ...x, label: e.target.value } : x) }))} className={inputCls} placeholder="Libellé" data-testid={`bonus-label-${i}`} />
                  <input type="number" step="0.01" value={b.amount} onChange={(e) => setInputs((s) => ({ ...s, bonuses: bonuses.map((x, j) => j === i ? { ...x, amount: e.target.value } : x) }))} className={`${inputCls} w-32`} placeholder="Montant" data-testid={`bonus-amount-${i}`} />
                  <button onClick={() => setInputs((s) => ({ ...s, bonuses: bonuses.filter((_, j) => j !== i) }))} className="text-red-400 px-1"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              ))}
            </div>
            <div>
              <div className="flex items-center justify-between mb-1"><span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Lignes manuelles</span>
                <button onClick={() => setInputs((s) => ({ ...s, manual_lines: [...manual, { label: "", amount: "", kind: "earning" }] }))} className="text-[11px] text-[#f7c935] flex items-center gap-1" data-testid="add-manual-btn"><Plus className="w-3 h-3" /> Ajouter</button></div>
              {manual.map((m, i) => (
                <div key={i} className="flex gap-2 mb-1">
                  <input value={m.label} onChange={(e) => setInputs((s) => ({ ...s, manual_lines: manual.map((x, j) => j === i ? { ...x, label: e.target.value } : x) }))} className={inputCls} placeholder="Libellé" />
                  <select value={m.kind} onChange={(e) => setInputs((s) => ({ ...s, manual_lines: manual.map((x, j) => j === i ? { ...x, kind: e.target.value } : x) }))} className={`${inputCls} w-44`}>
                    <option value="earning">Élément brut soumis</option><option value="reimbursement">Remboursement (net)</option><option value="deduction_net">Retenue (net)</option>
                  </select>
                  <input type="number" step="0.01" value={m.amount} onChange={(e) => setInputs((s) => ({ ...s, manual_lines: manual.map((x, j) => j === i ? { ...x, amount: e.target.value } : x) }))} className={`${inputCls} w-32`} placeholder="Montant" />
                  <button onClick={() => setInputs((s) => ({ ...s, manual_lines: manual.filter((_, j) => j !== i) }))} className="text-red-400 px-1"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              ))}
            </div>
            <label className="text-[11px] text-slate-400 block">Notes<input value={inputs.notes || ""} onChange={(e) => setNum("notes", e.target.value)} className={`${inputCls} mt-1`} data-testid="input-notes" /></label>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px]" data-testid="inputs-preview">
              {[["Brut", t.gross], ["Cotisations salariales", t.employee_contributions], ["Net imposable", t.net_taxable], [`PAS (${t.pas_rate} %)`, t.pas], ["Net avant impôt", t.net_before_tax], ["Net à payer", t.net_to_pay], ["Charges patronales", t.employer_contributions], ["Coût employeur", t.employer_cost]].map(([k, v]) => (
                <div key={k} className="bg-[#0b0811] border border-white/5 rounded-lg px-2.5 py-2"><div className="text-slate-500">{k}</div><div className={`font-mono font-bold ${k === "Net à payer" ? "text-[#f7c935]" : "text-white"}`}>{eur(v)}</div></div>
              ))}
            </div>
            <button onClick={() => setShowLines((v) => !v)} className="text-[11px] text-slate-400 hover:text-white" data-testid="toggle-lines-btn">{showLines ? "Masquer" : "Voir"} le détail des rubriques ({ps.lines.length})</button>
            {showLines && (
              <table className="w-full text-[10px]">
                <thead><tr className="text-slate-500 text-left"><th className="py-1">Rubrique</th><th className="text-right">Base</th><th className="text-right">Sal.</th><th className="text-right">Empl.</th></tr></thead>
                <tbody>{ps.lines.map((l, i) => (
                  <tr key={i} className="border-t border-white/5"><td className="py-0.5 text-slate-300">{l.label}</td><td className="text-right font-mono text-slate-500">{l.base ? l.base.toFixed(2) : ""}</td>
                    <td className="text-right font-mono">{l.emp_amount ? l.emp_amount.toFixed(2) : ""}</td><td className="text-right font-mono text-slate-400">{l.er_amount ? l.er_amount.toFixed(2) : ""}</td></tr>
                ))}</tbody>
              </table>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => onClose(changed)} className="px-3 py-2 text-xs text-slate-400 hover:text-white" data-testid="inputs-close-btn">Fermer</button>
              <button onClick={save} disabled={saving} className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#f7c935] text-[#0b0811] text-xs font-bold hover:bg-[#ffd75a] disabled:opacity-50" data-testid="inputs-save-btn">
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Enregistrer & recalculer
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
